import React, { useState, useEffect, useCallback } from "react";
import axios from 'axios';
import "../css/VendorStatus.css";
import "../css/VendorWorkflowDetail.css"; // Ensure this CSS file is included

// =========================================================================
// ICONS
// =========================================================================

// Edit Icon SVG (Kept for completeness, though unused in list view now)
const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="edit-icon">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
        <path d="m15 5 4 4" />
    </svg>
);

// Search Icon SVG
const SearchIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

// Check Icon for completed steps
const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
);

// Arrow Icon for active step
const ArrowRight = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
);


// =========================================================================
// MOCK API SETUP
// =========================================================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!API_BASE_URL) throw new Error('API base URL not set. Please set REACT_APP_API_BASE_URL in .env');
const api = axios.create({ baseURL: 'https://django-backend-115705416374.asia-south1.run.app/api' });
// const api = axios.create({baseURL: 'http://localhost:8000/api'});
api.interceptors.request.use(config => {
    const token = localStorage.getItem('access');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// =========================================================================
// VENDOR WORKFLOW DETAIL COMPONENT (New)
// =========================================================================

// Define the full workflow stages and how they map to the backend milestone.status
const WORKFLOW_STAGES = [
    { label: "Onboarding Initiated", requiredStatus: "PENDING" },
    { label: "Registration Complete", requiredStatus: "REGISTERED" },
    { label: "Awaiting Internal Approval", requiredStatus: "WAITING_FOR_APPROVAL" },
    { label: "Onboarded & Active", requiredStatus: "APPROVED" },
    { label: "Rejected", requiredStatus: "REJECTED" },
];

// Shared helper to format pending stage/approver display
const formatPendingLabel = (vendor) => {
    if (!vendor) return null;

    // Prefer detailed pending stage info if available
    const pendingStage = vendor.current_pending_stage;
    const pendingApproverEmail = Array.isArray(vendor.pending_approvers) && vendor.pending_approvers.length > 0
        ? vendor.pending_approvers[0].email
        : '';

    const stage =
        (pendingStage && (pendingStage.name || pendingStage.approval_name)) ||
        vendor.current_stage_name ||
        vendor.current_stage ||
        '';

    const approverEmail =
        pendingApproverEmail ||
        vendor.current_stage_approver ||
        vendor.waiting_for ||
        vendor.waiting_email ||
        '';

    if (!stage && !approverEmail) return null;
    if (stage && approverEmail) return `Pending: ${stage} (${approverEmail})`;
    if (stage) return `Pending: ${stage}`;
    return `Pending: ${approverEmail}`;
};

const formatPendingDetail = (vendor) => formatPendingLabel(vendor);

const VendorWorkflowDetail = ({ vendor, onBack }) => {
    const [progress, setProgress] = useState(null);
    const [pendingText, setPendingText] = useState(formatPendingDetail(vendor));
    const [rejectionInfo, setRejectionInfo] = useState(null); // 🆕 State for rejection details
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchProgress = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/vendor/workflow-status/?vendor_code=${vendor.vendor_code}`);
            setProgress(res.data);
            setRejectionInfo(res.data?.rejection_details || null); // 🆕 Capture rejection details

            // Prefer backend pending stage from workflow-status for accurate stage/email
            const stage = res.data?.current_pending_stage;
            const approvers = res.data?.pending_approvers || [];
            if (stage) {
                const stageName = stage.name || stage.approval_name || '';
                const email = approvers[0]?.email || '';
                if (stageName || email) {
                    setPendingText(`Pending: ${stageName}${email ? ` (${email})` : ''}`);
                }
            }
        } catch (e) {
            setError('Failed to load workflow status');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProgress(); }, [vendor.vendor_code]);

    // Prefer backend milestone if available, else fallback to vendor.status
    const [currentStatus, setCurrentStatus] = useState((vendor.status || '').toUpperCase());

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/vendor/workflow-status/?vendor_code=${vendor.vendor_code}`);
                const milestoneStatus = (res.data?.milestone?.status || vendor.status || '').toUpperCase();
                setCurrentStatus(milestoneStatus);
            } catch {
                setCurrentStatus((vendor.status || '').toUpperCase());
            }
        })();
    }, [vendor.vendor_code, vendor.status]);

    // Function to determine the state of a milestone
    const getStageState = (stageStatus, index, stages) => {
        const vendorStatusIndex = stages.findIndex(s => s.requiredStatus === currentStatus);

        // 🆕 Special handling for rejected vendors
        if (currentStatus === 'REJECTED') {
            const rejectedIndex = stages.findIndex(s => s.requiredStatus === 'REJECTED');
            if (index === rejectedIndex) {
                return 'rejected'; // Current rejected state
            } else if (index < 2) { // Onboarding Initiated and Registration Complete
                return 'complete'; // These stages were completed before rejection
            } else {
                return 'skipped'; // These stages were skipped due to rejection
            }
        }

        // ✅ For fully approved vendors, mark current stage as complete (green)
        if (currentStatus === 'APPROVED') {
            if (stageStatus === 'APPROVED') return 'complete';
        }

        if (index < vendorStatusIndex) {
            return 'complete'; // Already passed this stage
        } else if (index === vendorStatusIndex) {
            return 'active'; // This is the current stage
        } else {
            return 'pending'; // Future stage
        }
    };

    return (
        <div className="vwd-workflow-detail-container">
            <button onClick={onBack} className="vwd-back-button">
                &larr; Back to Vendor List
            </button>

            <h1 className="vwd-detail-header">
                Workflow Status: <span className="vwd-vendor-name-highlight">{vendor.vendor_name}</span>
            </h1>
            <p className="vwd-vendor-email-detail">Email: {vendor.vendor_email}</p>

            <div className="vwd-milestone-tracker">
                {(
                    currentStatus === 'APPROVED'
                        ? WORKFLOW_STAGES.filter(s => s.requiredStatus !== 'REJECTED') // hide rejected when fully approved
                        : WORKFLOW_STAGES
                ).map((stage, index, stages) => {
                    const state = getStageState(stage.requiredStatus, index, stages);
                    const isLast = index === stages.length - 1;

                    return (
                        <React.Fragment key={index}>
                            <div className={`vwd-milestone-step vwd-milestone-${state}`}>
                                <div className="vwd-milestone-icon">
                                    {state === 'complete' && <CheckIcon />}
                                    {state === 'active' && <ArrowRight />}
                                    {state === 'rejected' && <span>✗</span>}
                                    {state === 'skipped' && <span>—</span>}
                                    {state === 'pending' && <span>{index + 1}</span>}
                                </div>
                                <div className="vwd-milestone-info">
                                    <p className="vwd-milestone-label">{stage.label}</p>
                                    <p className="vwd-milestone-status">{state.toUpperCase()}</p>
                                </div>
                            </div>
                            {/* Connector Line */}
                            {!isLast && (
                                <div className={`vwd-milestone-connector vwd-connector-${state}`}></div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            <div className="vwd-detail-card">
                <h2>Current Stage Details</h2>

                {/* 🆕 REJECTION DETAILS BOX */}
                {currentStatus === 'REJECTED' && rejectionInfo && (
                    <div className="rejection-details-box" style={{
                        backgroundColor: '#fee2e2',
                        border: '1px solid #ef4444',
                        borderRadius: '8px',
                        padding: '16px',
                        marginTop: '10px',
                        marginBottom: '15px'
                    }}>
                        <p style={{ margin: '5px 0', color: '#b91c1c' }}><strong>🚫 Stage:</strong> {rejectionInfo.stage}</p>
                        <p style={{ margin: '5px 0', color: '#b91c1c' }}><strong>👤 Rejected By:</strong> {rejectionInfo.rejected_by}</p>
                        <p style={{ margin: '5px 0', color: '#b91c1c' }}><strong>💬 Reason:</strong> {rejectionInfo.reason}</p>
                        <p style={{ margin: '5px 0', color: '#7f1d1d', fontSize: '0.9em' }}>📅 Date: {new Date(rejectionInfo.rejected_at).toLocaleString()}</p>
                    </div>
                )}

                {pendingText && currentStatus !== 'REJECTED' && (
                    <p className="pending-line pending-detail-line">
                        {pendingText}
                    </p>
                )}

                <p>The vendor is currently at the **{WORKFLOW_STAGES.find(s => s.requiredStatus === currentStatus)?.label || 'Unknown'}** stage.</p>
                <p>Next Action Required: {
                    currentStatus === 'PENDING' ? 'The vendor needs to complete registration.' :
                        currentStatus === 'REGISTERED' ? 'Internal teams need to initiate the approval process.' :
                            currentStatus === 'WAITING_FOR_APPROVAL' ? 'Relevant approvers must review and sign off.' :
                                currentStatus === 'REJECTED' ? 'Vendor registration was rejected. No further action required.' :
                                    'Vendor is fully active and onboarded.'
                }</p>
            </div>

            {/* Internal Approval Progress - Only show if status is APPROVED (Active) */}
            {currentStatus === 'APPROVED' && (
                <div className="vwd-detail-card">
                    <h2>Internal Approval Progress</h2>
                    {loading && <p>Loading...</p>}
                    {error && <p className="error-message">{error}</p>}
                    {progress && (
                        <>
                            <div className="vwd-approval-milestones">
                                {(progress.nodes || []).map((node, index) => (
                                    <div key={node.sequence} className="vwd-approval-milestone">
                                        <div className="vwd-milestone-header">
                                            <div className="vwd-milestone-number">{node.sequence}</div>
                                            <div className="vwd-milestone-info">
                                                <h3 className="vwd-milestone-title">{node.name}</h3>
                                                <div className="vwd-milestone-meta">
                                                    <span className={`vwd-status-badge vwd-status-${node.status.toLowerCase()}`}>
                                                        {node.status}
                                                    </span>
                                                    <span className="vwd-progress-badge">{node.progress_percent}% Complete</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="vwd-assignees-section">
                                            <h4 className="vwd-assignees-title">Approvers</h4>
                                            <div className="vwd-assignees-grid">
                                                {(node.assignees || []).map(assignee => {
                                                    // Format timestamp for display
                                                    const formatTimestamp = (timestamp) => {
                                                        if (!timestamp) return null;
                                                        try {
                                                            const date = new Date(timestamp);
                                                            const year = date.getFullYear();
                                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                                            const day = String(date.getDate()).padStart(2, '0');
                                                            const hours = String(date.getHours()).padStart(2, '0');
                                                            const minutes = String(date.getMinutes()).padStart(2, '0');
                                                            const seconds = String(date.getSeconds()).padStart(2, '0');
                                                            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                                                        } catch (e) {
                                                            return null;
                                                        }
                                                    };

                                                    // Show timestamp only for approved, auto_approved, and rejected states
                                                    const showTimestamp = ['approved', 'auto_approved', 'rejected'].includes(assignee.state);
                                                    const formattedTime = showTimestamp && assignee.acted_at ? formatTimestamp(assignee.acted_at) : null;

                                                    return (
                                                        <div key={assignee.email} className={`vwd-assignee-card vwd-assignee-${assignee.state.toLowerCase().replace('_', '-')}`}>
                                                            <div className="vwd-assignee-avatar">
                                                                {assignee.state === 'approved' ? '✓' :
                                                                    assignee.state === 'auto_approved' ? '✓' :
                                                                        assignee.state === 'rejected' ? '✗' :
                                                                            assignee.state === 'auto_skipped' ? '—' : '○'}
                                                            </div>
                                                            <div className="vwd-assignee-details">
                                                                <span className="vwd-assignee-email">
                                                                    {assignee.email}
                                                                    {formattedTime && (
                                                                        <span className="vwd-assignee-timestamp"> (On {formattedTime})</span>
                                                                    )}
                                                                </span>
                                                                <span className={`vwd-assignee-status vwd-status-${assignee.state.toLowerCase().replace('_', '-')}`}>
                                                                    {assignee.state.replace('_', ' ').toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* <div className="vwd-activity-section">
                                <h3 className="vwd-activity-title">Recent Activity</h3>
                                <div className="vwd-activity-timeline">
                                    {(progress.history || []).map(activity => (
                                        <div key={activity.id} className="vwd-activity-item">
                                            <div className="vwd-activity-time">
                                                {new Date(activity.created_at).toLocaleString()}
                                            </div>
                                            <div className="vwd-activity-content">
                                                <span className="vwd-activity-action">{activity.action}</span>
                                                <span className="vwd-activity-node">Seq {activity.node_sequence} - {activity.node_name}</span>
                                                {activity.actor_email && (
                                                    <span className="vwd-activity-actor">by {activity.actor_email}</span>
                                                )}
                                                {activity.comment && (
                                                    <div className="vwd-activity-comment">"{activity.comment}"</div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div> */}
                        </>
                    )}
                    {!loading && !error && (!progress || (progress.nodes || []).length === 0) && (
                        <div className="vwd-empty-state">
                            <div className="vwd-empty-icon">📋</div>
                            <h3>No Internal Approval Data</h3>
                            <p>Internal approval progress will appear here once the process starts.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// =========================================================================
// VENDOR STATUS COMPONENT (Updated)
// =========================================================================

const VendorStatus = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [allVendors, setAllVendors] = useState([]); // Default list 
    const [vendors, setVendors] = useState([]); // Displayed list
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoLoaded, setAutoLoaded] = useState(false); // Track if auto-load was done

    // NEW STATE: Holds the vendor object selected for detail view
    const [selectedVendor, setSelectedVendor] = useState(null);

    // 🆕 NEW STATE: Track scroll position
    const [scrollPosition, setScrollPosition] = useState(0);

    // --- VIEW HANDLERS ---
    const handleCardClick = (vendor) => {
        // 🆕 Save current scroll position before navigating to detail view
        setScrollPosition(window.scrollY || window.pageYOffset);
        setSelectedVendor(vendor);
        // Don't scroll here - let useEffect handle it after render
    };

    const handleBackClick = () => {
        setSelectedVendor(null);
        // 🆕 Restore scroll position after returning to list view
        // Use setTimeout to ensure DOM has updated before scrolling
        setTimeout(() => {
            window.scrollTo(0, scrollPosition);
        }, 0);
    };

    // 🆕 Scroll to top when detail view opens
    useEffect(() => {
        if (selectedVendor) {
            // When detail view opens, always scroll to top
            window.scrollTo(0, 0);
        }
    }, [selectedVendor]);

    // --- AUTO-LOAD VENDORS BY APPROVER ON MOUNT ---
    const fetchVendorsByApprover = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // 🆕 Use new endpoint that includes rejected vendors for status tab
            const response = await api.get('/vendor-onboard-by-approver-with-rejected/');
            setAllVendors(response.data || []);
            setVendors(response.data || []);
            setAutoLoaded(true);
        } catch (err) {
            console.error("Failed to fetch vendors by approver:", err);
            // Don't show error if user is not an approver - just show empty state
            setAllVendors([]);
            setVendors([]);
            setAutoLoaded(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- FRONTEND SEARCH FILTERING LOGIC ---
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If empty search, display all currently loaded vendors
            setVendors(allVendors);
            return;
        }

        const query = searchTerm.toLowerCase().trim();

        // Perform instant client-side filtering and sorting
        const filtered = allVendors.filter(vendor => {
            const name = (vendor.vendor_name || '').toLowerCase();
            const code = (vendor.vendor_code || '').toLowerCase();
            const email = (vendor.vendor_email || '').toLowerCase();

            return name.includes(query) || code.includes(query) || email.includes(query);
        });

        // Smart sorting: Exact match -> Starts with -> Includes matches
        const sorted = filtered.sort((a, b) => {
            const aName = (a.vendor_name || '').toLowerCase();
            const bName = (b.vendor_name || '').toLowerCase();

            // Priority 1: Exact matches float to top
            if (aName === query && bName !== query) return -1;
            if (bName === query && aName !== query) return 1;

            // Priority 2: 'Starts with' the query 
            const aStarts = aName.startsWith(query);
            const bStarts = bName.startsWith(query);
            if (aStarts && !bStarts) return -1;
            if (bStarts && !aStarts) return 1;

            return 0; // Otherwise, maintain relative order
        });

        setVendors(sorted);

    }, [searchTerm, allVendors]);

    // Auto-load vendors by approver on component mount
    useEffect(() => {
        if (!autoLoaded) {
            fetchVendorsByApprover();
        }
    }, [autoLoaded, fetchVendorsByApprover]);

    const handleSearchClick = () => {
        // Since filtering is instantaneous via useEffect, clicking search isn't strictly necessary for fetching
        // However, we can keep it as a UI action placebo, or do nothing since search state is already applied.
    };


    // --- Helper for Status Styling (Updated) ---
    const getStatusClass = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'REGISTERED':
                return 'status-registered';
            case 'PENDING':
                return 'status-pending';
            case 'APPROVED':
                return 'status-approved';
            case 'WAITING_FOR_APPROVAL': // Added specific style for new status
                return 'status-waiting';
            case 'REJECTED': // 🆕 Added rejected status styling
                return 'status-rejected';
            default:
                return 'status-default';
        }
    };

    // -----------------------------------------------------------------
    // CONDITIONAL RENDERING: Show detail view if a vendor is selected
    // -----------------------------------------------------------------
    if (selectedVendor) {
        return (
            <VendorWorkflowDetail
                vendor={selectedVendor}
                onBack={handleBackClick}
            />
        );
    }

    // -----------------------------------------------------------------
    // LIST VIEW
    // -----------------------------------------------------------------
    return (
        <div className="vendor-status-container">
            <h3 className="status-header">Vendor OnBoarding Status Tracker</h3>

            <div className="search-controls-wrapper">
                <div className="search-input-group">
                    <input
                        type="text"
                        placeholder="Search Vendor Name (e.g., ABC Pvt Ltd)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="vendor-search-input"
                    />
                </div>

                <button
                    onClick={handleSearchClick}
                    className="search-button"
                    disabled={loading}
                >

                    <SearchIcon className="search-button-icon" />
                    Search
                </button>
            </div>

            <div className="vendor-list">
                {loading && <p className="loading-message">Loading vendor data...</p>}
                {error && <p className="error-message">{error}</p>}

                {!loading && !error && autoLoaded && (
                    <>
                        {vendors.length === 0 ? (
                            <p className="no-results">
                                {searchTerm ? `No vendors found matching "${searchTerm}".` : "No vendors assigned to you as an approver."}
                            </p>
                        ) : (
                            vendors.map((vendor) => (
                                <div
                                    key={vendor.vendor_code}
                                    className="vendor-card clickable-card" // Use 'clickable-card' for hover effect
                                    onClick={() => handleCardClick(vendor)} // <--- CLICK HANDLER ADDED
                                >
                                    <div className="vendor-info">
                                        <p className="vendor-name-display">{vendor.vendor_name}</p>
                                        <div className="detail-group">
                                            <span className="detail-label">Company:</span>
                                            <span className="detail-value">{vendor.company || 'N/A'}</span>
                                        </div>
                                        {/* <div className="detail-group">
                                            <span className="detail-label">Code:</span>
                                            <span className="detail-value">{vendor.vendor_code}</span>
                                        </div> */}
                                        <div className="detail-group">
                                            <span className="detail-label">Email:</span>
                                            <span className="detail-value">{vendor.vendor_email}</span>
                                        </div>

                                    </div>

                                    <div className="vendor-actions">
                                        <span className={`vendor-status-badge ${getStatusClass(vendor.status)}`}>
                                            {vendor.status.toUpperCase()}
                                        </span>
                                    </div>

                                    {/* 🆕 Show pending stage and approvers (data returned by backend) */}
                                    {/* Pending info simplified into pending-line above */}
                                </div>
                            ))
                        )}
                    </>
                )}

                {!autoLoaded && !loading && !error && vendors.length === 0 && (
                    <p className="initial-message">Loading your assigned vendors...</p>
                )}
            </div>
        </div>
    );
};

export default VendorStatus;